import api from './axios';
import type { PaginationParams } from '../types/api.types';
import type { UserRole, StaffMember, OpenWorkResult } from '../types/user.types';
import { NON_ASSIGNABLE_ROLES } from '@/constants/roles';

export interface MfaSettings {
  mfa_enabled: boolean;
  phone?: string;
}

export interface CreateUserDto {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: UserRole;
  link?: string;
}

export interface RolePermission {
  role: UserRole;
  capabilities: string[];
}

export interface PermissionEntry {
  _id: string;
  name: string;
  module: string;
  description: string;
}

export interface RoleLayer2Permissions {
  role_id: string;
  baselines: string[];
  layer2: string[];
}

export interface UserPermissionBreakdown {
  user_id: string;
  role: string;
  baselines: string[];
  layer2: string[];
  layer3_grants: string[];
  layer3_denials: string[];
  effective: string[];
}

export interface MyPermissions {
  role: string;
  baselines: string[] | '*';
  layer2: string[];
  layer3_grants: string[];
  layer3_denials: string[];
  effective: string[];
}

export interface WorkOverride {
  item_id: string;
  item_type: string;
  assignee_id: string;
}

export interface ReassignWorkDto {
  default_assignee_id: string;
  overrides?: WorkOverride[];
}

export async function listUsers(
  params: PaginationParams & { search?: string; role?: string },
): Promise<{ data: StaffMember[]; total: number; page: number; pageSize: number }> {
  const { data } = await api.get('/api/v2/enterprise/staff', { params });
  return data;
}

export async function inviteUser(body: CreateUserDto): Promise<{ status: string; message: string }> {
  const { data } = await api.post('/api/v2/enterprise/staff', body);
  return data;
}

export async function updateUserRole(id: string, role: UserRole): Promise<{ status: string }> {
  const { data } = await api.patch(`/api/v2/enterprise/staff/${id}/role`, { role });
  return data;
}

export async function updateAuthorityLimit(
  id: string,
  approval_authority_limit: number | null,
): Promise<{ status: string }> {
  const { data } = await api.patch(`/api/v2/enterprise/staff/${id}/authority-limit`, { approval_authority_limit });
  return data;
}

export async function deactivateUser(id: string): Promise<{ status: string }> {
  const { data } = await api.delete(`/api/v2/enterprise/staff/${id}`);
  return data;
}

export async function getStaffOpenWork(id: string): Promise<{ data: OpenWorkResult }> {
  const { data } = await api.get(`/api/v2/enterprise/staff/${id}/open-work`);
  return data;
}

export async function reassignStaffWork(id: string, body: ReassignWorkDto): Promise<{ status: string }> {
  const { data } = await api.post(`/api/v2/enterprise/staff/${id}/reassign`, body);
  return data;
}

export async function listStaffRoles(): Promise<{ _id: string; name: string }[]> {
  const { data } = await api.get('/api/v2/enterprise/staff/roles');
  // Normalize: backend may return plain array or wrapped { data: [] } / { roles: [] }
  let roles: { _id: string; name: string }[] = [];
  if (Array.isArray(data)) roles = data;
  else if (Array.isArray(data?.data)) roles = data.data;
  else if (Array.isArray(data?.roles)) roles = data.roles;
  return roles.filter((r) => !NON_ASSIGNABLE_ROLES.includes(r.name));
}

export async function suspendUser(id: string): Promise<{ status: string }> {
  const { data } = await api.put(`/api/v2/enterprise/staff/${id}/suspend`);
  return data;
}

export async function activateUser(id: string): Promise<{ status: string }> {
  const { data } = await api.put(`/api/v2/enterprise/staff/${id}/activate`);
  return data;
}

export async function hardDeleteStaff(id: string): Promise<{ status: string }> {
  const { data } = await api.delete(`/api/v2/enterprise/staff/${id}/hard`);
  return data;
}

export async function restoreStaff(id: string): Promise<{ status: string }> {
  const { data } = await api.patch(`/api/v2/enterprise/staff/${id}/restore`);
  return data;
}

export async function listDeletedStaff(params: {
  page?: number;
  pageSize?: number;
}): Promise<{ data: { staff: StaffMember[]; total: number; page: number; pageSize: number } }> {
  const { data } = await api.get('/api/v2/enterprise/staff/deleted', { params });
  return data;
}

export async function getRolePermissions(): Promise<RolePermission[]> {
  return [];
}

// -------------------------------------------------------
// RBAC — Master permission list
// -------------------------------------------------------

export async function listAllPermissions(): Promise<PermissionEntry[]> {
  const { data } = await api.get('/api/v2/enterprise/rbac/permissions');
  return data?.data ?? [];
}

// -------------------------------------------------------
// RBAC — Current user effective permissions
// -------------------------------------------------------

export async function getMyPermissions(): Promise<MyPermissions> {
  const { data } = await api.get('/api/v2/enterprise/rbac/me');
  return data?.data;
}

// -------------------------------------------------------
// RBAC — Layer 2: Role-level additions
// -------------------------------------------------------

export async function getRoleLayer2Permissions(roleId: string): Promise<RoleLayer2Permissions> {
  const { data } = await api.get(`/api/v2/enterprise/rbac/roles/${roleId}/permissions`);
  return data?.data;
}

export async function addRolePermission(roleId: string, permission_name: string): Promise<void> {
  await api.post(`/api/v2/enterprise/rbac/roles/${roleId}/permissions`, { permission_name });
}

export async function removeRolePermission(roleId: string, permissionName: string): Promise<void> {
  await api.delete(`/api/v2/enterprise/rbac/roles/${roleId}/permissions/${permissionName}`);
}

// -------------------------------------------------------
// RBAC — Layer 3: User-level grants and denials
// -------------------------------------------------------

export async function getUserPermissions(userId: string): Promise<UserPermissionBreakdown> {
  const { data } = await api.get(`/api/v2/enterprise/rbac/users/${userId}/permissions`);
  return data?.data;
}

export async function addUserGrant(userId: string, permission_name: string): Promise<void> {
  await api.post(`/api/v2/enterprise/rbac/users/${userId}/permissions/grant`, { permission_name });
}

export async function removeUserGrant(userId: string, permissionName: string): Promise<void> {
  await api.delete(`/api/v2/enterprise/rbac/users/${userId}/permissions/grant/${permissionName}`);
}

export async function addUserDenial(userId: string, permission_name: string): Promise<void> {
  await api.post(`/api/v2/enterprise/rbac/users/${userId}/permissions/deny`, { permission_name });
}

export async function removeUserDenial(userId: string, permissionName: string): Promise<void> {
  await api.delete(`/api/v2/enterprise/rbac/users/${userId}/permissions/deny/${permissionName}`);
}

export async function enableMfa(): Promise<void> {
  await api.put('/api/v2/rbac/mfa/enable');
}

export async function verifyMfaPhone(body: { code: string }): Promise<void> {
  await api.post('/api/v2/rbac/mfa/confirm-enable', { otp: body.code });
}

export async function disableMfa(): Promise<void> {
  await api.put('/api/v2/rbac/mfa/disable');
}
